import { useState, useEffect, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Loader2, Plus, Search, Link as LinkIcon, CreditCard, Check, X, ChevronLeft, ChevronRight } from 'lucide-react';
import { useAdmin } from '@/hooks/useAdmin';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_META = {
  draft: { label: 'Skica', color: 'bg-gray-200 text-gray-700' },
  pending: { label: 'Čeka plaćanje', color: 'bg-amber-100 text-amber-800' },
  paid: { label: 'Plaćeno', color: 'bg-emerald-100 text-emerald-800' },
  active: { label: 'Aktivno', color: 'bg-blue-100 text-blue-800' },
  expired: { label: 'Isteklo', color: 'bg-gray-300 text-gray-600' },
  rejected: { label: 'Odbijeno', color: 'bg-red-100 text-red-700' },
};

function StatusPill({ status }) {
  const meta = STATUS_META[status] || { label: status || '—', color: 'bg-gray-100 text-gray-600' };
  return <span className={`inline-block px-2 py-0.5 text-[11px] font-semibold rounded ${meta.color}`} data-testid={`sponsored-status-${status}`}>{meta.label}</span>;
}

function LinkArticleModal({ open, onClose, submission, authFetch, onLinked }) {
  const [q, setQ] = useState('');
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [days, setDays] = useState(30);
  const [label, setLabel] = useState('Sponzorirano');
  const [activate, setActivate] = useState(true);
  const [linking, setLinking] = useState(false);

  useEffect(() => {
    if (!open) { setQ(''); setResults([]); return; }
  }, [open]);

  useEffect(() => {
    if (!open) return;
    if (!q.trim()) { setResults([]); return; }
    const h = setTimeout(async () => {
      setSearching(true);
      try {
        const r = await authFetch(`${API}/admin/articles?q=${encodeURIComponent(q)}&limit=10`);
        if (r?.ok) {
          const d = await r.json();
          setResults(d.articles || d.items || d || []);
        }
      } catch { /* noop */ }
      finally { setSearching(false); }
    }, 250);
    return () => clearTimeout(h);
  }, [q, open, authFetch]);

  async function link(articleId) {
    setLinking(true);
    try {
      const r = await authFetch(`${API}/admin/sponsored-submissions/${submission.id}/link-article`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ article_id: articleId, days: Number(days) || 30, label: label || 'Sponzorirano', activate_now: !!activate }),
      });
      if (!r?.ok) { toast.error('Povezivanje nije uspjelo'); return; }
      toast.success('Članak povezan');
      onLinked?.();
      onClose?.();
    } catch (e) { toast.error('Greška: ' + e.message); }
    finally { setLinking(false); }
  }

  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" role="dialog">
      <div className="bg-white dark:bg-slate-900 rounded-xl w-full max-w-2xl max-h-[85vh] flex flex-col" data-testid="link-article-modal">
        <div className="flex items-center justify-between px-5 py-3 border-b border-gray-200 dark:border-slate-700">
          <h3 className="text-base font-semibold">Poveži članak — {submission?.title || submission?.id?.slice(0, 8)}</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-700" aria-label="Zatvori"><X size={18} /></button>
        </div>
        <div className="p-5 space-y-4 overflow-auto">
          <div className="relative">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input autoFocus value={q} onChange={e => setQ(e.target.value)} placeholder="Pretraži po naslovu…"
              className="w-full pl-9 pr-3 py-2 text-sm border border-gray-300 dark:border-slate-700 rounded-lg outline-none focus:border-amber-400 bg-transparent"
              data-testid="link-article-search" />
          </div>
          <div className="grid grid-cols-3 gap-3 text-sm">
            <label className="flex flex-col">
              <span className="text-xs text-gray-500 mb-1">Trajanje (dani)</span>
              <input type="number" min="1" max="365" value={days} onChange={e => setDays(e.target.value)}
                className="px-3 py-2 border border-gray-300 dark:border-slate-700 rounded-lg outline-none bg-transparent" data-testid="link-days" />
            </label>
            <label className="flex flex-col col-span-2">
              <span className="text-xs text-gray-500 mb-1">Oznaka</span>
              <input value={label} onChange={e => setLabel(e.target.value)} maxLength={32}
                className="px-3 py-2 border border-gray-300 dark:border-slate-700 rounded-lg outline-none bg-transparent" data-testid="link-label" />
            </label>
          </div>
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={activate} onChange={e => setActivate(e.target.checked)} data-testid="link-activate-now" />
            Odmah aktiviraj (postavi članak kao sponzoriran)
          </label>
          <div className="border border-gray-200 dark:border-slate-700 rounded-lg divide-y divide-gray-100 dark:divide-slate-800 max-h-[40vh] overflow-auto">
            {searching && <div className="p-4 text-sm text-gray-400 flex items-center gap-2"><Loader2 size={14} className="animate-spin" /> Tražim…</div>}
            {!searching && results.length === 0 && q && <div className="p-4 text-sm text-gray-400">Nema rezultata</div>}
            {!searching && results.map(a => (
              <button key={a.id} onClick={() => link(a.id)} disabled={linking}
                className="w-full flex items-center justify-between gap-3 px-4 py-2.5 text-left hover:bg-amber-50 dark:hover:bg-amber-900/20 disabled:opacity-50"
                data-testid={`link-article-row-${a.id}`}>
                <div className="min-w-0">
                  <div className="text-sm font-medium truncate">{a.title_hr || a.title || '—'}</div>
                  <div className="text-xs text-gray-500 truncate">/clanak/{a.slug}</div>
                </div>
                <LinkIcon size={14} className="text-amber-600 shrink-0" />
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

export default function AdminSponsoredOrders() {
  const { authFetch } = useAdmin();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState('all');
  const [q, setQ] = useState('');
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [linkTarget, setLinkTarget] = useState(null);

  const pageSize = 20;
  const pages = useMemo(() => Math.max(1, Math.ceil(total / pageSize)), [total]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const qs = new URLSearchParams({ page: String(page), page_size: String(pageSize) });
      if (status && status !== 'all') qs.set('status', status);
      if (q.trim()) qs.set('q', q.trim());
      const r = await authFetch(`${API}/admin/sponsored-submissions?${qs}`);
      if (r?.ok) {
        const d = await r.json();
        setRows(d.rows || d.submissions || d.items || []);
        setTotal(d.total || 0);
      } else {
        setRows([]); setTotal(0);
      }
    } catch { setRows([]); setTotal(0); }
    finally { setLoading(false); }
  }, [authFetch, page, status, q]);

  useEffect(() => { load(); }, [load]);

  async function markPaid(id) {
    const amt = prompt('Iznos (EUR)?', '100');
    if (amt === null) return;
    try {
      const r = await authFetch(`${API}/admin/sponsored-submissions/${id}/mark-paid`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ amount_eur: Number(amt) || 0 }),
      });
      if (r?.ok) { toast.success('Označeno kao plaćeno'); load(); }
      else toast.error('Neuspjelo');
    } catch (e) { toast.error(e.message); }
  }

  async function reject(id) {
    if (!window.confirm('Odbaci ovu prijavu?')) return;
    try {
      const r = await authFetch(`${API}/admin/sponsored-submissions/${id}/reject`, { method: 'POST' });
      if (r?.ok) { toast.success('Odbijeno'); load(); }
      else toast.error('Neuspjelo');
    } catch (e) { toast.error(e.message); }
  }

  return (
    <div className="p-4 md:p-6" data-testid="admin-sponsored-orders-page">
      <header className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div>
          <h1 className="text-xl md:text-2xl font-bold text-gray-900 dark:text-gray-100">Sponzorirane narudžbe</h1>
          <p className="text-sm text-gray-500">Sve sponzorirane prijave — trijaža, povezivanje s člankom i označavanje plaćenih.</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <select value={status} onChange={e => { setPage(1); setStatus(e.target.value); }}
            className="px-3 py-2 text-sm bg-white dark:bg-slate-900 border border-gray-300 dark:border-slate-700 rounded-lg" data-testid="sponsored-status-filter">
            <option value="all">Svi statusi</option>
            <option value="draft">Skica</option>
            <option value="pending">Čeka plaćanje</option>
            <option value="paid">Plaćeno</option>
            <option value="active">Aktivno</option>
            <option value="expired">Isteklo</option>
            <option value="rejected">Odbijeno</option>
          </select>
          <input value={q} onChange={e => setQ(e.target.value)} onKeyDown={e => { if (e.key === 'Enter') { setPage(1); load(); } }}
            placeholder="Pretraži naslov/email…"
            className="px-3 py-2 text-sm border border-gray-300 dark:border-slate-700 bg-white dark:bg-slate-900 rounded-lg"
            data-testid="sponsored-search" />
        </div>
      </header>

      <div className="bg-white dark:bg-slate-900 border border-gray-200 dark:border-slate-700 rounded-xl overflow-hidden">
        <div className="overflow-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 dark:bg-slate-800 text-xs uppercase text-gray-500">
              <tr>
                <th className="text-left px-4 py-2">Datum</th>
                <th className="text-left px-4 py-2">Naslov / email</th>
                <th className="text-left px-4 py-2">Status</th>
                <th className="text-right px-4 py-2">Iznos</th>
                <th className="text-left px-4 py-2">Članak</th>
                <th className="text-right px-4 py-2">Akcije</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan="6" className="text-center py-8 text-gray-400"><Loader2 className="inline animate-spin mr-2" size={14} />Učitavanje…</td></tr>
              ) : rows.length === 0 ? (
                <tr><td colSpan="6" className="text-center py-8 text-gray-400">Nema prijava</td></tr>
              ) : rows.map(r => (
                <tr key={r.id} className="border-t border-gray-100 dark:border-slate-800" data-testid={`sponsored-row-${r.id}`}>
                  <td className="px-4 py-2 whitespace-nowrap text-gray-500 text-xs">{r.created_at ? new Date(r.created_at).toLocaleDateString('hr-HR') : '—'}</td>
                  <td className="px-4 py-2">
                    <div className="font-medium text-gray-900 dark:text-gray-100">{r.title || r.headline || '—'}</div>
                    <div className="text-xs text-gray-500">{r.contact_email || r.email || '—'}</div>
                  </td>
                  <td className="px-4 py-2"><StatusPill status={r.status || r.payment_status || 'draft'} /></td>
                  <td className="px-4 py-2 text-right font-medium">{r.amount_paid_eur ? `${Number(r.amount_paid_eur).toFixed(2)} €` : (r.amount_eur ? `${Number(r.amount_eur).toFixed(2)} €` : '—')}</td>
                  <td className="px-4 py-2">
                    {r.article_id ? (
                      <a href={`/admin/articles/${r.article_id}/edit`} className="text-amber-600 hover:underline text-xs" data-testid={`sponsored-article-link-${r.id}`}>Otvori</a>
                    ) : <span className="text-gray-400 text-xs">—</span>}
                  </td>
                  <td className="px-4 py-2 text-right">
                    <div className="inline-flex gap-1">
                      <button onClick={() => setLinkTarget(r)} title="Poveži članak"
                        className="p-1.5 text-xs text-amber-600 hover:bg-amber-50 dark:hover:bg-amber-900/20 rounded" data-testid={`sponsored-link-btn-${r.id}`}>
                        <LinkIcon size={14} />
                      </button>
                      {r.status !== 'paid' && r.status !== 'active' && (
                        <button onClick={() => markPaid(r.id)} title="Označi kao plaćeno"
                          className="p-1.5 text-xs text-emerald-600 hover:bg-emerald-50 dark:hover:bg-emerald-900/20 rounded" data-testid={`sponsored-mark-paid-${r.id}`}>
                          <Check size={14} />
                        </button>
                      )}
                      {r.status !== 'rejected' && (
                        <button onClick={() => reject(r.id)} title="Odbaci"
                          className="p-1.5 text-xs text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded" data-testid={`sponsored-reject-${r.id}`}>
                          <X size={14} />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {total > pageSize && (
          <div className="flex items-center justify-between px-4 py-2 border-t border-gray-100 dark:border-slate-800 text-xs text-gray-500">
            <span>Stranica {page} / {pages} · ukupno {total}</span>
            <div className="flex gap-1">
              <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page <= 1}
                className="px-2 py-1 border rounded disabled:opacity-50" data-testid="sponsored-prev"><ChevronLeft size={14} /></button>
              <button onClick={() => setPage(p => Math.min(pages, p + 1))} disabled={page >= pages}
                className="px-2 py-1 border rounded disabled:opacity-50" data-testid="sponsored-next"><ChevronRight size={14} /></button>
            </div>
          </div>
        )}
      </div>

      <LinkArticleModal open={!!linkTarget} onClose={() => setLinkTarget(null)} submission={linkTarget} authFetch={authFetch} onLinked={load} />
    </div>
  );
}
